import React, { useState } from "react";

/**
 * ChipSelect — a quiet chip-picker used across every Nervous System
 * stage. Supports multi- and single-select, plus an optional
 * "Something else" free-text tail that appends to the selection.
 */
export default function ChipSelect({
  options,
  value,
  onChange,
  mode = "multi", // 'multi' | 'single'
  allowOther = true,
  otherPlaceholder = "Something else…",
  testidPrefix = "ns-chip",
}) {
  const [other, setOther] = useState("");

  const arr = Array.isArray(value) ? value : value ? [value] : [];
  const isSelected = (o) => arr.includes(o);

  const toggle = (o) => {
    if (mode === "single") {
      onChange(isSelected(o) ? null : o);
      return;
    }
    onChange(isSelected(o) ? arr.filter((x) => x !== o) : [...arr, o]);
  };

  const addOther = () => {
    const t = other.trim();
    if (!t) return;
    if (mode === "single") {
      onChange(t);
    } else if (!arr.includes(t)) {
      onChange([...arr, t]);
    }
    setOther("");
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-group`}>
        {options.map((o) => {
          const label = typeof o === "string" ? o : o.label;
          const val = typeof o === "string" ? o : o.key;
          const active = isSelected(val);
          return (
            <button
              key={val}
              type="button"
              onClick={() => toggle(val)}
              data-testid={`${testidPrefix}-${val.replace(/\s+/g, "-").toLowerCase()}`}
              aria-pressed={active}
              className={`px-3.5 py-1.5 rounded-full text-[13px] border hairline transition-all ${
                active
                  ? "bg-[hsl(var(--copper))]/15 text-[hsl(var(--fg))] border-[hsl(var(--copper))]/50"
                  : "text-[hsl(var(--fg))]/75 hover:bg-[hsl(var(--sand))]/60 border-[hsl(var(--line))]"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {allowOther && (
        <div className="mt-4 flex items-center gap-2">
          <input
            type="text"
            value={other}
            onChange={(e) => setOther(e.target.value)}
            placeholder={otherPlaceholder}
            data-testid={`${testidPrefix}-other-input`}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addOther();
              }
            }}
            className="flex-1 bg-transparent border-b border-[hsl(var(--line))] py-2 text-[14px] focus:outline-none focus:border-[hsl(var(--copper))] placeholder:text-[hsl(var(--muted-foreground))]/70"
          />
          <button
            type="button"
            onClick={addOther}
            data-testid={`${testidPrefix}-other-add`}
            className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--copper))] hover:opacity-80 disabled:opacity-40"
            disabled={!other.trim()}
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}
